// Sub-accounts: a broker's holdings and a person's loans. An account has at
// most one parent, and the parent's type decides what it holds: an
// `investment` account holds `stock` and `funds` holdings, a `lend` account
// (a person) holds `lend` loans.

import { formatMoney } from './formatters';

const HOLDING_TYPES = new Set(['stock', 'funds']);

export const isHolding = (a) => a?.parent_account_id != null && HOLDING_TYPES.has(a.account_type);
export const isLoan = (a) => a?.parent_account_id != null && a.account_type === 'lend';
export const isSettledLoan = (a) => isLoan(a) && a.loan?.status === 'settled';

// Child types a parent of this type takes (none when it takes none).
export const CHILD_TYPES = { investment: ['stock', 'funds'], lend: ['lend'] };
// The parent type a child of this type may belong to.
export const PARENT_TYPE = { stock: 'investment', funds: 'investment', lend: 'lend' };

const byName = (x, y) => (x || '').localeCompare(y || '', undefined, { sensitivity: 'base' });

/**
 * Loans in the order a person lists them: open first, then settled, each
 * oldest first. Holdings by symbol.
 */
export function sortChildren(children) {
  return [...children].sort((a, b) => {
    if (isLoan(a) && isLoan(b)) {
      const sa = isSettledLoan(a) ? 1 : 0;
      const sb = isSettledLoan(b) ? 1 : 0;
      if (sa !== sb) return sa - sb;
      return (a.loan?.first_date || '').localeCompare(b.loan?.first_date || '') || byName(a.name, b.name);
    }
    return byName(a.instrument_symbol || a.name, b.instrument_symbol || b.name);
  });
}

/**
 * A flat account list as rows with their children: `[{ account, children }]`,
 * in the list's own order. A child whose parent is not in the list (filtered
 * out, or closed and hidden) stands as a row of its own.
 */
export function nestAccounts(list) {
  const ids = new Set(list.map((a) => a.id));
  const childrenOf = new Map();
  list.forEach((a) => {
    if (a.parent_account_id != null && ids.has(a.parent_account_id)) {
      if (!childrenOf.has(a.parent_account_id)) childrenOf.set(a.parent_account_id, []);
      childrenOf.get(a.parent_account_id).push(a);
    }
  });
  return list
    .filter((a) => a.parent_account_id == null || !ids.has(a.parent_account_id))
    .map((a) => ({ account: a, children: sortChildren(childrenOf.get(a.id) || []) }));
}

/** An account's children in the whole account list. */
export const childrenOf = (accounts, id) => sortChildren((accounts || []).filter((a) => a.parent_account_id === id));

/** The one entry of a per-currency summary to show: the given currency's, else the first. */
export const pickSummary = (rows, currency) => (rows || []).find((r) => r.currency === currency) || (rows || [])[0] || null;

/** An account's own balance in each currency, from its balance buckets: `{ currency: amount }`. */
export function balanceByCurrency(buckets, accountId) {
  const totals = {};
  (buckets || []).forEach((b) => {
    if (b.account_id !== accountId || !b.currency) return;
    totals[b.currency] = (totals[b.currency] || 0) + (b.total || 0);
  });
  Object.keys(totals).forEach((c) => { totals[c] = Math.round(totals[c] * 100) / 100; });
  return totals;
}

// The allocation names this many holdings; the rest share one line.
const ALLOCATION_LINES = 5;

/**
 * `n` of `currency` in the display currency `convert` names (`{ target,
 * rates }`, rates in USD per unit as `useUsdRates` gives them), or null when
 * either currency has no rate. Without `convert`, `n` itself.
 */
export function inDisplay(convert, currency, n) {
  if (!convert || n == null) return n;
  const from = convert.rates?.[currency];
  const to = convert.rates?.[convert.target];
  return from == null || !to ? null : (n * from) / to;
}

/**
 * What a broker holds, one entry per currency, largest first: its cash, its
 * holdings at market value (at cost while one has no price), the gain of the
 * priced ones over their cost, and the allocation, each part's share of what
 * is there. A negative cash balance lowers the total but takes no share.
 * With `convert`, one entry in its display currency, at today's rates; a
 * currency with no rate is left out, as the account chart leaves it out.
 *
 * @param {object} cash - the broker's own balance, `{ currency: amount }`
 * @param {object[]} holdings - the broker's holdings, with their positions
 * @param {object} [convert] - `{ target, rates }`, the page's display currency
 */
export function brokerValue(cash, holdings, convert = null) {
  const byCurrency = new Map();
  const entry = (currency) => {
    const key = convert ? convert.target : currency;
    if (!byCurrency.has(key)) {
      byCurrency.set(key, {
        currency: key, cash: 0, holdingsValue: 0, gain: 0, pricedCost: 0, holdings: [], priceDate: null,
        isConverted: false, cashIsConverted: false, holdingsIsConverted: false,
      });
    }
    const e = byCurrency.get(key);
    // MI-329: a figure is approximate only when it was in another currency.
    if (convert && currency !== convert.target) e.isConverted = true;
    return e;
  };
  Object.entries(cash || {}).forEach(([currency, amount]) => {
    const n = inDisplay(convert, currency, amount);
    if (n != null && Math.abs(amount || 0) >= 0.005) {
      const e = entry(currency);
      e.cash += n;
      if (convert && currency !== convert.target) e.cashIsConverted = true;
    }
  });
  (holdings || []).forEach((h) => {
    const p = h.position || {};
    const currency = p.currency || h.currencies?.[0];
    if (!currency) return;
    const value = inDisplay(convert, currency, p.market_value ?? p.cost ?? 0);
    if (value == null) return;
    const e = entry(currency);
    e.holdingsValue += value;
    if (convert && currency !== convert.target) e.holdingsIsConverted = true;
    if (p.market_value != null) {
      e.gain += inDisplay(convert, currency, p.unrealized_gain || 0);
      e.pricedCost += inDisplay(convert, currency, p.cost || 0);
    }
    if (p.price_date && (!e.priceDate || p.price_date > e.priceDate)) e.priceDate = p.price_date;
    e.holdings.push({ holding: h, value });
  });
  return [...byCurrency.values()].map((e) => {
    const parts = e.holdings
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value)
      .map((x) => ({ key: x.holding.id, label: x.holding.instrument_symbol || x.holding.name, value: x.value }));
    const shown = parts.slice(0, ALLOCATION_LINES);
    const rest = parts.slice(ALLOCATION_LINES);
    if (rest.length) shown.push({ key: 'other', label: null, value: rest.reduce((s, x) => s + x.value, 0) });
    if (e.cash > 0) shown.push({ key: 'cash', label: null, value: e.cash });
    const base = shown.reduce((s, x) => s + x.value, 0);
    const share = (value) => (base > 0 && value > 0 ? (value / base) * 100 : null);
    return {
      currency: e.currency,
      isConverted: e.isConverted,
      cashIsConverted: e.cashIsConverted,
      holdingsIsConverted: e.holdingsIsConverted,
      cash: e.cash,
      holdingsValue: e.holdingsValue,
      total: e.cash + e.holdingsValue,
      gain: e.pricedCost > 0 ? e.gain : null,
      gainPct: e.pricedCost > 0 ? (e.gain / e.pricedCost) * 100 : null,
      priceDate: e.priceDate,
      allocation: shown.map((x) => ({ ...x, pct: base > 0 ? (x.value / base) * 100 : 0 })),
      // Every holding's own share, past the fifth too, and the cash's.
      shares: Object.fromEntries(parts.map((x) => [x.key, share(x.value)])),
      cashShare: share(e.cash),
    };
  }).sort((a, b) => b.total - a.total);
}

/** Units, with as many decimals as a fractional share or a coin needs. */
export function formatUnits(n) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 8 }).format(Number(n) || 0);
}

/** A price per unit: two decimals, more when it is below one. */
export function formatPrice(n) {
  const value = Number(n) || 0;
  if (Math.abs(value) >= 1 || value === 0) return formatMoney(value, null, true);
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(value);
}

/**
 * The trade side a movement would carry: a Transfer into a holding is a buy,
 * out of one a sell, and Income on one a receive. Null when it touches no
 * holding, or moves between two holdings (which has no single side).
 */
export function tradeSide(movement, accounts = []) {
  const find = (id) => (id == null ? null : accounts.find((a) => a.id === id));
  if (movement?.type === 'Transfer') {
    const into = isHolding(find(movement.to_account_id));
    const outOf = isHolding(find(movement.from_account_id));
    if (into && outOf) return null;
    if (into) return 'buy';
    if (outOf) return 'sell';
    return null;
  }
  if (movement?.type === 'Income' && isHolding(find(movement.account_id))) return 'receive';
  return null;
}

/**
 * "2 SPY @ 150.00" for a movement with a trade, else null. The symbol comes
 * from the holding (the movement's own leg, or the accounts list); the price
 * is the printed one, else the amount over the units.
 */
export function tradeLabel(movement, accounts = []) {
  const trade = movement?.trade;
  const quantity = Number(trade?.quantity);
  if (!trade || !quantity) return null;
  const holding = [movement.to_account, movement.from_account, movement.account, ...(accounts || [])]
    .find((a) => a && a.id === trade.holding_account_id);
  const symbol = holding?.instrument_symbol || holding?.name || '';
  const price = trade.unit_price != null ? trade.unit_price : Math.abs(Number(movement.amount) || 0) / quantity;
  return `${formatUnits(quantity)} ${symbol} @ ${formatPrice(price)}`.replace(/\s+@/, ' @');
}

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Split a payment received from a person across their open loans, as the
 * payment form proposes it: the oldest open loan first, up to what each still
 * owes; whatever is left once every open loan is paid is interest on the last
 * loan paid. With no open loan the whole payment goes to the person's own
 * balance. The owner edits the parts before saving.
 *
 * @param {number} received - what the person paid
 * @param {object[]} openLoans - the person's open loans in the payment's currency
 * @param {number} personId - the person's own account
 * @returns {{ account_id: number, principal: number, interest: number }[]}
 */
export function proposePaymentParts(received, openLoans, personId) {
  let remaining = round2(Number(received) || 0);
  if (remaining <= 0) return [];
  const loans = [...(openLoans || [])]
    .filter((l) => (l.loan?.owed || 0) > 0)
    .sort((a, b) => (a.loan?.first_date || '').localeCompare(b.loan?.first_date || '') || a.id - b.id);
  const parts = [];
  for (const loan of loans) {
    if (remaining <= 0) break;
    const principal = round2(Math.min(remaining, loan.loan.owed));
    parts.push({ account_id: loan.id, principal, interest: 0 });
    remaining = round2(remaining - principal);
  }
  if (remaining > 0) {
    if (parts.length) parts[parts.length - 1].interest = remaining;
    else parts.push({ account_id: personId, principal: remaining, interest: 0 });
  }
  return parts;
}
